import { useEffect, useRef } from 'react';
import type { UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { type IntakeResponse } from '../../services/api';
import type {
  KinnserClinicDataResponse,
  KinnserPersonInfo,
} from '../../services/api/types.gen';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import { createNameMatcher } from '../../utils/fuzzyMatch';
import type { CreatePatientFormData } from './types';
import { useKinnserClinicDataShared } from './useKinnserClinicDataShared';

interface UseClinicDataOptions {
  agencySystemName: string | undefined;
  kinnserClinicId: string | null | undefined;
  selectedIntake: IntakeRecord | null;
  setValue: UseFormSetValue<CreatePatientFormData>;
  trigger: UseFormTrigger<CreatePatientFormData>;
  intakeId: string | undefined;
  saveToServer: (updates: Partial<CreatePatientFormData>) => Promise<void>;
}

interface UseClinicDataReturn {
  clinics: KinnserPersonInfo[];
  clinicData: KinnserClinicDataResponse | null;
  loadingClinics: boolean;
  loadingClinicData: boolean;
}

/**
 * Hook for fetching and managing Kinnser clinic data.
 * Uses the shared global cache to prevent duplicate API calls.
 * Handles clinic list fetching, clinic data loading, and fuzzy matching
 * for physicians and insurance providers.
 */
export function useClinicData({
  agencySystemName,
  kinnserClinicId,
  selectedIntake,
  setValue,
  trigger,
  intakeId,
  saveToServer,
}: UseClinicDataOptions): UseClinicDataReturn {
  // Use the shared hook for cached clinic data
  const { clinics, clinicData, loadingClinics, loadingClinicData } =
    useKinnserClinicDataShared({
      agencySystemName,
      kinnserClinicId,
    });

  // Track previous clinic ID to detect user-initiated changes vs initial load
  const previousClinicIdRef = useRef<string | null | undefined>(undefined);

  // Track if auto-select has been done for this intake
  const autoSelectDoneRef = useRef(false);

  // Reset auto-select flag when intakeId changes
  useEffect(() => {
    autoSelectDoneRef.current = false;
  }, [intakeId]);

  // Auto-select clinic if there's only one and not already set
  useEffect(() => {
    if (autoSelectDoneRef.current) return;
    if (loadingClinics || clinics.length !== 1) return;

    const intakeData = selectedIntake as unknown as IntakeResponse;
    if (!intakeData?.kinnser_clinic_id) {
      autoSelectDoneRef.current = true;
      setValue('kinnser_clinic_id', clinics[0].id);
      saveToServer({ kinnser_clinic_id: clinics[0].id }).catch(err => {
        console.error('Failed to auto-save clinic selection:', err);
      });
    }
  }, [
    clinics,
    loadingClinics,
    selectedIntake,
    setValue,
    saveToServer,
    intakeId,
  ]);

  // Trigger validation after clinic data is loaded
  useEffect(() => {
    if (clinicData && !loadingClinicData) {
      setTimeout(() => trigger(), 0);
    }
  }, [clinicData, loadingClinicData, trigger]);

  // Handle fuzzy matching when clinic data or intake data changes
  useEffect(() => {
    if (!clinicData || !selectedIntake) return;

    const intakeData = selectedIntake as unknown as IntakeResponse;

    // Collect auto-matched values to save in one API call
    const autoMatchedValues: Partial<CreatePatientFormData> = {};

    // Auto-select branch if there's only one and not already set
    if (clinicData.branches?.length === 1 && !intakeData?.kinnser_branch_id) {
      setValue('kinnser_branch_id', clinicData.branches[0].id);
      autoMatchedValues.kinnser_branch_id = clinicData.branches[0].id;
    }

    // Fuzzy match physician only if no kinnser_physician_id is already set
    const referringPhysician = selectedIntake?.referring_physician;

    if (
      referringPhysician &&
      clinicData.physicians?.length &&
      !selectedIntake?.kinnser_physician_id
    ) {
      const matcher = createNameMatcher(clinicData.physicians);
      const match = matcher.findBestMatch(referringPhysician);

      if (match) {
        console.log(
          `Auto-matching physician "${referringPhysician}" to "${match.name}"`
        );
        setValue('kinnser_physician_id', match.id);
        autoMatchedValues.kinnser_physician_id = match.id;
      }
    }

    // Save all auto-matched values in one API call
    if (Object.keys(autoMatchedValues).length > 0) {
      saveToServer(autoMatchedValues);
    }
  }, [clinicData, selectedIntake, saveToServer, setValue]);

  // Track clinic ID changes (reset logic moved to handleClinicFieldBlur in useCreatePatientForm)
  useEffect(() => {
    if (kinnserClinicId && previousClinicIdRef.current === undefined) {
      previousClinicIdRef.current = kinnserClinicId;
    } else if (
      kinnserClinicId &&
      previousClinicIdRef.current !== kinnserClinicId
    ) {
      previousClinicIdRef.current = kinnserClinicId;
    }
  }, [kinnserClinicId]);

  return {
    clinics,
    clinicData,
    loadingClinics,
    loadingClinicData,
  };
}
